import React, { useState, useCallback, useMemo, ComponentType } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import useInfiniteOptions from "@/components_v2/FormFields/Dropdown/hooks/useInfiniteOptions";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import {
  Option,
  InfiniteOptionsDropdownProps,
  BaseDropdownProps,
} from "@/components_v2/FormFields/Dropdown/types";

import { BasePaginationQueryParams } from "@/redux/types";

export function withInfiniteOptions<
  T,
  C extends BasePaginationQueryParams,
  P extends BaseDropdownProps,
>(
  WrappedComponent: ComponentType<P>,
): ComponentType<InfiniteOptionsDropdownProps<T, C>> {
  return function WithInfiniteOptionsComponent({
    fetchFn,
    labelFieldName,
    labelFormatter,
    onFetchSuccess,
    valueFieldName,
    searchFieldName,
    debounceMs = 300,
    defaultFilters = {},
    multiple,
    value,
    onChange,
    className,
    ...props
  }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const {
      options,
      isLoading,
      search,
      setSearch,
      handleScroll,
      resetSearch,
      fetchOptions,
      initialValueFetched,
    } = useInfiniteOptions<T, C>({
      fetchFn,
      defaultFilters,
      labelFieldName,
      labelFormatter,
      onFetchSuccess,
      valueFieldName,
      searchFieldName,
      debounceMs,
      initialValue: value as string | string[] | null,
    });

    const handleDropdownVisibilityChange = useCallback(
      (visible: boolean) => {
        setIsOpen(visible);

        if (visible) {
          fetchOptions();
        } else {
          resetSearch();
        }
      },
      [fetchOptions, resetSearch],
    );

    const handleChange = useCallback(
      (value: string | number | (string | number)[]) => {
        if (onChange) {
          onChange(value);
        }
        if (search) {
          setSearch("");
        }
        if (!multiple) {
          setIsOpen(false);
        }
      },
      [onChange, search, multiple, setSearch],
    );

    const currentValue = useMemo(() => {
      if (value && !initialValueFetched) {
        return multiple ? [] : undefined;
      }

      return value !== undefined ? value : multiple ? [] : undefined;
    }, [value, initialValueFetched, multiple]);

    const emptyContent = useMemo(() => {
      if (isLoading) {
        return <BaseSpinner size="small" />;
      }

      return (
        <div className="text-center p-4">
          <InboxOutlined className="text-2xl mb-2" />
          <p>{t("noData")}</p>
        </div>
      );
    }, [isLoading, t]);

    return (
      <WrappedComponent
        {...(props as unknown as P)}
        options={options as Option[]}
        loading={isLoading}
        onSearch={setSearch}
        searchValue={search}
        filterOption={false}
        onPopupScroll={handleScroll}
        onChange={handleChange}
        value={currentValue}
        onDropdownVisibleChange={handleDropdownVisibilityChange}
        open={isOpen}
        multiple={multiple}
        notFoundContent={emptyContent}
        className={`base-dropdown ${className || ""}`}
      />
    );
  };
}

export default withInfiniteOptions;
